import type { NodeViewProps } from "@tiptap/react";
import { NodeViewContent, NodeViewWrapper } from "@tiptap/react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { FieldUiOptions, SectionNodeAttrs } from "../types";
import { FieldHeader } from "./field-header";
import { FieldPlaceholder } from "./field-placeholder";
import { useDocumentHasOwnerOverrides } from "./field-view-state";
import { useQualityGuardFieldIssues } from "./use-quality-guard-field-issues";

/**
 * Shared NodeView for all section-type nodes (SectionTitle, ParagraphSection, RichSection).
 *
 * Renders a non-editable label heading above the editable content area.
 * Shows contextual placeholder text when the section is empty.
 * Styling adapts based on the node type:
 * - sectionTitle: Subtle label, content styled as a prominent title
 * - paragraphSection/richSection: Bold section heading, normal content
 */
interface SectionNodeViewProps extends NodeViewProps {
  fieldUiOptions?: FieldUiOptions;
}

export function SectionNodeView({
  node,
  editor,
  decorations,
  updateAttributes,
  fieldUiOptions,
}: SectionNodeViewProps) {
  const { fieldId, label, guidance, expectedMentionTypes, ownerId, chromeless, _diffStatus } =
    node.attrs as SectionNodeAttrs;
  const [guidanceVisible, setGuidanceVisible] = useState(false);
  const isTitle = node.type.name === "sectionTitle";
  const hasOwnerOverrides = useDocumentHasOwnerOverrides(editor);
  const entityOwnerId = fieldUiOptions?.getEntityOwnerId?.() ?? null;
  const showGuardPlaceholder = fieldUiOptions?.getShouldShowGuardPlaceholder?.() ?? false;
  const qualityGuardIssues = useQualityGuardFieldIssues(fieldUiOptions, fieldId);

  // Detect empty state for placeholder display.
  // - sectionTitle (inline*): empty when there's no inline content
  // - paragraphSection/richSection (paragraph+): empty when there's a single empty paragraph
  //   (once the user adds a second paragraph or types text, placeholder hides)
  const isEmpty = isTitle
    ? node.content.size === 0
    : node.childCount === 1 &&
      node.firstChild?.type.name === "paragraph" &&
      node.firstChild.content.size === 0;

  const hasGuidance = typeof guidance === "string" && guidance.trim().length > 0;
  const firstHeaderSelected = decorations.some(
    (decoration) => decoration.type.attrs.class === "df-first-section-header-selected"
  );

  const placeholderText = hasGuidance ? guidance : `Enter ${label.toLowerCase()}...`;
  const isChromeless = chromeless === true;

  return (
    <NodeViewWrapper
      data-type={node.type.name}
      data-df-section-anchor=""
      data-field-id={fieldId}
      className={cn(
        firstHeaderSelected && "df-first-section-header-selected",
        isChromeless ? "py-0" : isTitle ? "mb-10" : "pt-5 pb-5 first:pt-0"
      )}
    >
      {isChromeless ? null : (
        <div contentEditable={false} draggable={false} className="document-field-chrome">
          <FieldHeader
            fieldId={fieldId}
            label={label}
            guidance={guidance}
            expectedMentionTypes={expectedMentionTypes}
            diffStatus={_diffStatus}
            ownerId={ownerId}
            entityOwnerId={entityOwnerId}
            showGuardPlaceholder={showGuardPlaceholder}
            qualityGuardIssues={qualityGuardIssues}
            showOwner={hasOwnerOverrides}
            onOwnerChange={(nextOwnerId) => {
              // Dual write by design: updateAttributes keeps the owner in the editor doc so the next
              // versioned save persists it, while onAssignSectionOwner commits it to the entity
              // immediately and emits the assignment notification.
              updateAttributes({ ownerId: nextOwnerId });
              fieldUiOptions?.onAssignSectionOwner?.({
                fieldId,
                label,
                ownerId: nextOwnerId,
              });
            }}
            canEditOwner={editor.isEditable}
            showMeatball={editor.isEditable}
            showGuidanceCallout={hasGuidance && guidanceVisible}
            onToggleGuidance={hasGuidance ? () => setGuidanceVisible((v) => !v) : undefined}
            onAskClaudia={fieldUiOptions?.onAskClaudia}
            onOpenQualityGuard={fieldUiOptions?.onOpenQualityGuard}
            className="mb-3"
            titleClassName={
              isTitle
                ? "text-[18px] font-semibold text-foreground"
                : "text-[18px] font-semibold text-foreground"
            }
          />
        </div>
      )}

      <div className="grid">
        <NodeViewContent
          className={cn(
            "[grid-area:1/1]",
            isTitle
              ? "text-[28px] font-semibold leading-tight text-foreground outline-none"
              : "outline-none"
          )}
        />
        {isEmpty ? <FieldPlaceholder text={placeholderText} isTitle={isTitle} /> : null}
      </div>
    </NodeViewWrapper>
  );
}
